import { useCallback, useMemo } from 'react';
import _ from 'lodash';
import queryString, { ParsedQuery } from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { filterEmptyFilters } from '../helpers/helpers';
import { ISurveyFilters } from '../services/surveys';

export const useFilters = () => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const currentFilters = useMemo(() => {
    const properQuery = search.substring(1);
    return queryString.parse(properQuery);
  }, [search]);

  const getFiltersWithExceptions = useCallback(
    (exceptions: string[]) => {
      return Object.keys(currentFilters).reduce((res: Record<string, unknown>, key: string) => {
        const isKeyInExceptions = exceptions.includes(key);
        if (isKeyInExceptions) {
          return res;
        }
        return { ...res, [key]: currentFilters[key] };
      }, {});
    },
    [currentFilters]
  );

  const applyFilters = (filter: Record<string, string | null | undefined>) => {
    const newFiltersState = { ...currentFilters, ...filter };
    const filteredFilters = filterEmptyFilters(newFiltersState as unknown as ISurveyFilters);
    const newFiltersQuery = queryString.stringify(filteredFilters);
    navigate({
      search: `?${newFiltersQuery}`,
    });
  };

  const applyManyNestedFilters = (
    filterArray: { id: string; filter: Record<string, unknown> }[],
    key?: string,
    additionalFilters?: Record<string, string | null | undefined>
  ) => {
    const nestedKey = key || 'members';
    const nestedFilters = currentFilters[nestedKey];
    const nestedObj = nestedFilters ? JSON.parse(nestedFilters as string) : {};
    const objReadyForStringify = filterArray.reduce(
      (resp: Record<string, unknown>, filterObj) => {
        const currentNestedFilters = nestedObj[filterObj.id] ? nestedObj[filterObj.id] : {};
        const newNestedFilters = { ...currentNestedFilters, ...filterObj.filter };
        const filteredFilters = filterEmptyFilters(newNestedFilters);
        return !_.isEmpty(filteredFilters)
          ? { ...resp, [filterObj.id]: filteredFilters }
          : _.omit(resp, filterObj.id);
      },
      { ...nestedObj }
    );
    const stringifiedObject = !_.isEmpty(objReadyForStringify)
      ? JSON.stringify(objReadyForStringify)
      : null;
    return applyFilters({ [nestedKey]: stringifiedObject, ...additionalFilters });
  };

  const applyNestedFilter = useCallback(
    (filterObj: Record<string, unknown>, id: string, key?: string) => {
      const nestedKey = key || 'members';
      const nestedFilters = currentFilters[nestedKey];
      const nestedObj = nestedFilters ? JSON.parse(nestedFilters as string) : {};
      const currentNestedFilters = nestedObj[id] ? nestedObj[id] : {};
      const newNestedFilters = { ...currentNestedFilters, ...filterObj };
      const filteredFilters = filterEmptyFilters(newNestedFilters);
      const objReadyForStringify = !_.isEmpty(filteredFilters)
        ? { ...nestedObj, [id]: filteredFilters }
        : _.omit(nestedObj, id);
      const stringifiedObject = !_.isEmpty(objReadyForStringify)
        ? JSON.stringify(objReadyForStringify)
        : null;
      return applyFilters({ [nestedKey]: stringifiedObject });
    },
    [currentFilters, applyFilters]
  );

  const getNestedFilters = (key: string) => {
    const nestedFilter = currentFilters[key];
    if (typeof nestedFilter === 'string') {
      return JSON.parse(nestedFilter);
    }
    return {};
  };

  const manageTripleSortFilters = (value: string | null | undefined, key: string) => {
    switch (value) {
      case 'default': {
        const sortOrder = JSON.stringify({ [key]: 'asc' });
        return applyFilters({ sortOrder });
      }
      case 'asc': {
        const sortOrder = JSON.stringify({ [key]: 'desc' });
        return applyFilters({ sortOrder });
      }
      case 'desc': {
        const sortOrder = JSON.stringify({ [key]: 'default' });
        return applyFilters({ sortOrder });
      }
      default: {
        const sortOrder = JSON.stringify({ [key]: 'asc' });
        return applyFilters({ sortOrder });
      }
    }
  };

  const applyAndMergeFilters = (parentKey: string, key: string, value: unknown) => {
    const val = value ?? null;
    const thisFilter = currentFilters[parentKey];
    if (thisFilter) {
      // @ts-expect-error query-string types a value as string | string[]
      const filters = JSON.parse(thisFilter);
      const filtersObj = { ...filters, ...{ [key]: val } };
      const filteredStatesVal = filterEmptyFilters(filtersObj);
      const filtersValue = JSON.stringify(filteredStatesVal);
      return applyFilters({ [parentKey]: filtersValue });
    }
    const filterValue = JSON.stringify({ [key]: val });
    applyFilters({ [parentKey]: filterValue });
  };

  return {
    currentFilters,
    getFiltersWithExceptions,
    getNestedFilters,
    applyFilters,
    applyNestedFilter,
    applyManyNestedFilters,
    manageTripleSortFilters,
    applyAndMergeFilters,
  };
};
